import { Card, Table, Badge, Button } from "react-bootstrap";
import { RiMapPinLine, RiFileCopyLine } from "react-icons/ri";
import { TbSend, TbChevronDown, TbChevronRight } from "react-icons/tb";
import { useState } from "react";
import CustomPagination from "../../components/common/CustomPagination";
import { errorAlert, successAlert } from "../../components/common/Alert";
import { SendGateCodetoDriver } from "../../services/Networkcall";
import moment from "moment";
import Swal from "sweetalert2";
import { UpdateBookingModal } from "../../components/dashboard/UpdateBookingModal";

const getBookingStatus = (startDateTime, endDateTime) => {
    if (!startDateTime || !endDateTime) return "N/A";
    const now = moment.utc();
    const start = moment.utc(startDateTime);
    const end = moment.utc(endDateTime);
    if (now.isAfter(end)) return "Expired";
    if (now.isBefore(start)) return "Upcoming";
    if (now.isBetween(start, end)) {
        return end.diff(now, "days") <= 3 ? "Expiring Soon" : "Active";
    }
    return "N/A";
};

const statusClass = (status) =>
({
    Active: "bg-success-subtle text-success",
    Expired: "bg-danger-subtle text-danger",
    Upcoming: "bg-primary-subtle text-primary",
    "Expiring Soon": "bg-warning-subtle text-warning",
}[status] || "");

// ── Child row ──────────────────────────────────────────────────────────────
function ChildRow({ item, isTextCopied, onCopy, sendingGateCode, onSend, setRefetch }) {
    const tz = item?.location_info?.location_timezone || moment.tz.guess();
    const status = getBookingStatus(item?.booking_start_date, item?.booking_end_date);
    const [showUpdateBookingModal, setShowUpdateBookingModal] = useState(false);


    return (<>
        <tr className="gc-child-row cursor-pointer" onClick={() => setShowUpdateBookingModal(true)}>
            {/* Gate Code */}
            <td className="gc-code text-primary ps-4">
                {!item?.uniFiAccessPin ? (
                    <span className="text-muted fst-italic">processing...</span>
                ) : (
                    <>
                        {item.uniFiAccessPin}
                        <span className="position-relative d-inline-block">
                            <RiFileCopyLine
                                className="cursor-pointer ms-2 mb-1"
                                onClick={(e) => {
                                    e.stopPropagation();  
                                    onCopy(item.uniFiAccessPin, item._id);
                                }}
                            />
                            {isTextCopied === item._id && (
                                <span
                                    className="position-absolute bottom-100 start-50 translate-middle-x mb-1 px-2 py-1 rounded bg-dark text-white"
                                    style={{ fontSize: "0.75rem", whiteSpace: "nowrap", zIndex: 10 }}
                                >
                                    Copied!
                                </span>
                            )}
                        </span>
                    </>
                )}
            </td>

            {/* Driver */}
            <td>
                {item?.driver ? (<>
                    <strong>{item?.driver?.userName || "—"}</strong>
                    <p className="mb-0">{item?.driver?.phoneNumber || ""}</p></>
                ) : (<small className="text-center text-muted">Not assigned</small>)}
            </td>

            {/* Truck */}
            <td>
                {item?.vehicle_info ? (<>
                    <strong>{item?.vehicle_info?.vehicleName || "—"}</strong>
                    <p className="mb-0">{item?.vehicle_info?.licensePlate || ""}</p></>
                ) : (<small className="text-center text-muted">Not assigned</small>)}
            </td>

            {/* Yard */}
            <td>
                <RiMapPinLine /> {item?.location_info?.locationName || "—"}
            </td>

            {/* Reservation */}
            <td>
                {item?.booking_start_date && (
                    <>
                        <p className="mb-0">{moment(item.booking_start_date).tz(tz).format("MMM DD, hh:mm A")} –</p>
                        <p className="mb-0">{moment(item.booking_end_date).tz(tz).format("MMM DD, hh:mm A")}</p>
                    </>
                )}
            </td>

            {/* Created */}
            <td>{item?.createdAt ? moment(item.createdAt).format("MMM DD, hh:mm A") : "N/A"}</td>

            {/* Expires */}
            <td>{item?.booking_end_date ? moment(item.booking_end_date).tz(tz).format("MMM DD, hh:mm A") : "N/A"}</td>


            {/* Action */}
            {/* Action */}
            <td>
                <Button
                    variant="primary"
                    size="sm"
                    onClick={(e) => {
                        e.stopPropagation();
                        onSend(item?._id);
                    }}
                    disabled={sendingGateCode || status === "Expired" || !item?.driver}
                >
                    <TbSend /> {sendingGateCode === item?._id ? "Sending..." : "Send Gate Code"}
                </Button>
            </td>
        </tr>
        {showUpdateBookingModal && <UpdateBookingModal show={showUpdateBookingModal} closeModal={() => setShowUpdateBookingModal(false)} booking={item} setRefetch={setRefetch} />}
    </>);
}

// ── Parent summary row ─────────────────────────────────────────────────────
function ParentRow({ group, isExpanded, onToggle }) {
    const tz = group?.location_info?.location_timezone || moment.tz.guess();
    const status = getBookingStatus(group?.booking_start_date, group?.booking_end_date);
    const total = group?.bookingCount || group?.bookings?.length || 0;
    const assignVehicles = group?.bookings.filter((vcl) => vcl.vehicle_info);
    const assignDrivers = group?.bookings.filter((dr) => dr.driver);


    return (
        <tr
            className="gc-parent-row"
            onClick={onToggle}
            style={{ cursor: "pointer", background: "var(--bs-gray-100)" }}
        >
            {/* Expand toggle */}
            <td className="fw-semibold text-primary">
                <span className="me-1">
                    {isExpanded ? <TbChevronDown size={15} /> : <TbChevronRight size={15} />}
                </span>
                {total} booking{total !== 1 ? "s" : ""}
            </td>

            {/* Driver */}
            <td>
                <strong>{`${assignDrivers?.length || "0"} / ${total}`}</strong>
            </td>

            {/* Truck */}
            <td>
                <strong>{`${assignVehicles.length || "0"} / ${total}`}</strong>
            </td>

            {/* Yard */}
            <td>
                <RiMapPinLine /> {group?.location_info?.locationName || "—"}
            </td>

            {/* Reservation */}
            <td>
                {group?.booking_start_date && (
                    <>
                        <p className="mb-0">{moment(group.booking_start_date).tz(tz).format("MMM DD, hh:mm A")} –</p>
                        <p className="mb-0">{moment(group.booking_end_date).tz(tz).format("MMM DD, hh:mm A")}</p>
                    </>
                )}
            </td>

            {/* Created */}
            <td>{group?.createdAt ? moment(group.createdAt).format("MMM DD, hh:mm A") : "N/A"}</td>

            {/* Expires */}
            <td>{group?.booking_end_date ? moment(group.booking_end_date).tz(tz).format("MMM DD, hh:mm A") : "N/A"}</td>

            {/* Status */}
            <td className="text-center">
                <Badge className={`strong p-2 rounded-3 gc-status fw-semibold ${statusClass(status)}`}>
                    {status}
                </Badge>
            </td>
        </tr>
    );
}

// ── Main component ─────────────────────────────────────────────────────────
export default function GateCodesTableMultiple({ bookings, border = false, pagination, pageHandler, setRefetch }) {
    const [expandedRows, setExpandedRows] = useState({});
    const [isTextCopied, setIsTextCopied] = useState(null);
    const [sendingGateCode, setSendingGateCode] = useState(null);

    const toggleRow = (id) => setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));

    const CopyGateCode = (gateCode, id) => {
        navigator.clipboard.writeText(gateCode);
        setIsTextCopied(id);
        setTimeout(() => setIsTextCopied(null), 1000);
    };

    const handleSendGateCode = async (id) => {
        setSendingGateCode(id);
        try {
            const result = await Swal.fire({
                title: "Send Gate Code?",
                text: "The driver will receive the gate code via email. Please confirm.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Ok",
                cancelButtonText: "Cancel",
                customClass: {
                    popup: "custom-swal-popup",
                    title: "custom-swal-title",
                    htmlContainer: "custom-swal-text",
                    confirmButton: "custom-swal-confirm",
                    cancelButton: "custom-swal-cancel",
                },
            });
            if (!result.isConfirmed) return;
            const res = await SendGateCodetoDriver({ bookingId: id });
            if (res?.success) {
                successAlert({ message: res?.message || "Gate code sent successfully" });
            } else {
                errorAlert({ message: res?.message || "Failed to send gate code" });
            }
        } catch (err) {
            errorAlert({ message: err?.message || "Failed to send gate code" });
        } finally {
            setSendingGateCode(null);
        }
    };

    return (
        <Card className={`gc-table ${border ? "mt-3" : "border-0"}`}>
            

            <div className="table-responsive">
                <Table hover>
                    <thead>
                        <tr>
                            <th>Bookings</th>
                            <th>Driver</th>
                            <th>Truck / Plate</th>
                            <th>Yard</th>
                            <th>Reservation</th>
                            <th>Created</th>
                            <th>Expires</th>
                            <th className="text-center">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {bookings?.length > 0 ? (
                            bookings.map((group, i) => {
                                const groupId = group?.groupId || group?._id || i;
                                const isExpanded = !!expandedRows[groupId];
                                return (
                                    <>
                                        <ParentRow
                                            key={`parent-${groupId}`}
                                            group={group}
                                            isExpanded={isExpanded}
                                            onToggle={() => toggleRow(groupId)}
                                        />
                                        {isExpanded && (
                                            <>
                                                <tr key={`child-hdr-${groupId}`} className="table-secondary">
                                                    <th className="ps-4" >Gate Code</th>
                                                    <th>Driver</th>
                                                    <th>Truck / Plate</th>
                                                    <th>Yard</th>
                                                    <th>Reservation</th>
                                                    <th>Created</th>
                                                    <th>Expires</th>
                                                    <th className="text-center">Action</th>
                                                </tr>
                                                {(group?.bookings || []).map((item, j) => (
                                                    <ChildRow
                                                        key={`child-${groupId}-${j}`}
                                                        item={item}
                                                        isTextCopied={isTextCopied}
                                                        onCopy={CopyGateCode}
                                                        sendingGateCode={sendingGateCode}
                                                        onSend={handleSendGateCode}
                                                        setRefetch={setRefetch}
                                                    />
                                                ))}
                                            </>
                                        )}
                                    </>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={9} className="text-center py-5">No bookings found.</td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            <CustomPagination pagination={pagination} pageHandler={pageHandler} />
        </Card>
    );
}